// Push-to-talk recorder UI: a press-and-hold mic button (slide up to lock, slide left to cancel)
// and the recording bar it expands into (live waveform + timer + delete / pause-resume / send).
// State + audio transport live in useVoiceMode; this module is the gesture + presentation layer.
import { useRef } from 'react';
import { MicIcon, SendIcon, TrashIcon, PauseIcon, PlayIcon } from '@/components/chat/ChatIcons';

/** PTT state + actions, owned by useVoiceMode and threaded through MessageComposer. */
export interface PttControls {
  /** A recording is active (held or locked, including while paused). */
  recording: boolean;
  /** Recording is locked (finger released) — the explicit delete/pause/send bar is shown. */
  locked: boolean;
  /** Recording is paused (capture stopped, buffer kept). */
  paused: boolean;
  /** Elapsed recorded time in whole seconds. */
  seconds: number;
  /** Recent input levels (0..1), oldest→newest, for the live waveform. */
  levels: number[];
  /** The paused take is currently playing back through the speaker. */
  playing: boolean;
  start: () => void;
  send: () => void;
  cancel: () => void;
  pause: () => void;
  resume: () => void;
  lock: () => void;
  /** Play back the recorded (paused) take. */
  play: () => void;
  /** Stop playback of the paused take. */
  stopPlayback: () => void;
}

// Gesture thresholds (px from the press origin).
const LOCK_DY = -56; // slide up past this → lock
const CANCEL_DX = -72; // slide left past this → cancel
const MIN_MS = 500; // release sooner than this → treat as an accidental tap (cancel, don't send)

/**
 * The idle mic button. Press & hold to record; release to send. While held, sliding up locks
 * (hands-free) and sliding left cancels. Move/up are tracked on `window` (not this element) so the
 * composer can swap to the recording bar mid-gesture without dropping the pointer.
 */
export function PttMicButton({
  start,
  send,
  cancel,
  lock,
}: Pick<PttControls, 'start' | 'send' | 'cancel' | 'lock'>) {
  const st = useRef({ x: 0, y: 0, t: 0, active: false });
  const fns = useRef<{ move?: (e: PointerEvent) => void; up?: (e: PointerEvent) => void; cancelEvt?: (e: PointerEvent) => void }>({});

  const detach = () => {
    if (fns.current.move) window.removeEventListener('pointermove', fns.current.move);
    if (fns.current.up) window.removeEventListener('pointerup', fns.current.up);
    if (fns.current.cancelEvt) window.removeEventListener('pointercancel', fns.current.cancelEvt);
    fns.current = {};
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return; // left-click / touch / pen only
    e.preventDefault();
    st.current = { x: e.clientX, y: e.clientY, t: Date.now(), active: true };

    const move = (ev: PointerEvent) => {
      if (!st.current.active) return;
      const dx = ev.clientX - st.current.x;
      const dy = ev.clientY - st.current.y;
      if (dx <= CANCEL_DX) { st.current.active = false; detach(); cancel(); return; }
      if (dy <= LOCK_DY) { st.current.active = false; detach(); lock(); return; }
    };
    const up = () => {
      if (!st.current.active) return;
      st.current.active = false;
      const elapsed = Date.now() - st.current.t;
      detach();
      if (elapsed < MIN_MS) cancel(); // too short to be intentional speech
      else send();
    };
    // pointercancel (e.g. the OS hijacks the touch for a scroll/gesture) → discard, never send.
    const cancelEvt = () => {
      if (!st.current.active) return;
      st.current.active = false;
      detach();
      cancel();
    };

    fns.current = { move, up, cancelEvt };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancelEvt);
    start();
  };

  return (
    <button
      type="button"
      onPointerDown={onPointerDown}
      title="Hold to talk · slide up to lock · slide left to cancel"
      style={{ touchAction: 'none' }}
      className="w-9 h-9 rounded-full flex items-center justify-center text-ui-text3 hover:text-ui-text transition flex-shrink-0"
    >
      <MicIcon className="h-5 w-5" />
    </button>
  );
}

function Waveform({ levels }: { levels: number[] }) {
  return (
    <div className="flex flex-1 items-center gap-[2px] h-6 overflow-hidden justify-end">
      {levels.map((l, i) => (
        <span
          key={i}
          className="w-[2px] rounded-full bg-ui-text3/80 flex-shrink-0"
          style={{ height: `${Math.max(2, Math.round(l * 24))}px` }}
        />
      ))}
    </div>
  );
}

function fmt(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** The bar shown while a PTT recording is active — replaces the normal composer input row. */
export function PttRecorder({ locked, paused, playing, seconds, levels, send, cancel, pause, resume, play, stopPlayback }: PttControls) {
  return (
    <div className="flex-shrink-0 px-4 pb-4 pt-2 border-t border-ui-border">
      <div className="flex flex-col gap-2 bg-ui-subtle border border-ui-border rounded-2xl px-3 py-2" style={{ touchAction: 'none' }}>
        {/* status row: blinking red mic + timer + live waveform */}
        <div className="flex items-center gap-3">
          {paused ? (
            <button
              type="button"
              onClick={playing ? stopPlayback : play}
              title={playing ? 'Stop playback' : 'Play recording'}
              aria-label={playing ? 'Stop playback' : 'Play recording'}
              className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center bg-white/90 text-brand-dark hover:bg-white transition"
            >
              {playing ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}
            </button>
          ) : (
            <span className="flex-shrink-0 text-red-500 animate-pulse">
              <MicIcon className="h-5 w-5" />
            </span>
          )}
          <span className="w-10 text-xs tabular-nums text-ui-text3">{fmt(seconds)}</span>
          <Waveform levels={levels} />
        </div>

        {/* controls row: explicit buttons once locked, slide hints while still held */}
        {locked ? (
          <div className="flex items-center justify-between">
            <button
              onClick={cancel}
              title="Delete recording"
              className="w-9 h-9 rounded-full flex items-center justify-center text-ui-text3 hover:text-red-400 transition flex-shrink-0"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
            <button
              onClick={paused ? resume : pause}
              title={paused ? 'Resume recording' : 'Pause recording'}
              className="w-11 h-11 rounded-full flex items-center justify-center border-2 border-red-500 text-red-500 hover:bg-red-500/10 transition flex-shrink-0"
            >
              {paused ? <MicIcon className="h-5 w-5" /> : <PauseIcon className="h-5 w-5" />}
            </button>
            <button
              aria-label="Send voice message"
              onClick={send}
              className="w-9 h-9 rounded-full bg-brand-dark flex items-center justify-center text-white hover:bg-brand-dark/80 transition flex-shrink-0"
            >
              <SendIcon className="h-5 w-5" />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-5 text-[11px] text-ui-text2">
            <span>‹ Slide to cancel</span>
            <span>Slide up to lock ⌃</span>
          </div>
        )}
      </div>
    </div>
  );
}
